import { useCallback, useMemo } from "react";
import { useMaskedInput } from "@tellescope/react-hooks";
import { maskDateInput, parseMaskedDateInput } from "@tellescope/utils";

export interface UseMaskedDateInputArgs {
  maskedValue: string;
  includeTime?: boolean;
  onCommit: (parsed: Date | null) => void;
}

/**
 * Applies the numeric date mask to a form field's text input, committing a value only when the
 * entry is a finished date. A half-typed mask is not an answer — committing on every keystroke
 * would clear an existing value as soon as the user began editing it — so intermediate states
 * update the text and nothing else.
 *
 * @returns `value` and input handlers to spread, plus `parsed` for rendering what the digits
 * currently mean (null until the entry is complete).
 */
export const useMaskedDateInput = ({ maskedValue, includeTime, onCommit }: UseMaskedDateInputArgs) => {
  const mask = useCallback((raw: string) => maskDateInput(raw, { includeTime }), [includeTime]);
  const parse = useCallback((masked: string) => parseMaskedDateInput(masked, { includeTime }), [includeTime]);

  const onMaskedChange = useCallback(
    (masked: string, previous: string) => {
      if (masked === "") {
        if (previous !== "") onCommit(null);
        return;
      }

      const parsed = parse(masked);
      if (parsed) onCommit(parsed);
    },
    [onCommit, parse]
  );

  const { value, ...inputProps } = useMaskedInput({ value: maskedValue, mask, onMaskedChange });

  return { value, parsed: useMemo(() => parse(value), [parse, value]), ...inputProps };
};
